import React, { useState } from 'react';
import styled from 'styled-components';
import { FaLock, FaCheck } from 'react-icons/fa';
import { Input } from '../../../components/ui/Input/Input';
import { PasswordInput } from '../../../components/ui/Input/PasswordInput';
import { Button } from '../../../components/ui/Button/Button';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { resetSenhaService } from '../services/passwordService';
import logger from '../../../utils/logger';

const Form = styled.form`
  width: 100%;
`;

const PasswordRequirements = styled.div`
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.8rem;
    margin: 1rem 0;
  }
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.$valid ? '#4CAF50' : colors.textMuted};
  font-size: 0.85rem;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${props => props.$valid ? '#4CAF50' : colors.borderLight};
    font-size: 0.8rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.8rem;
    gap: 0.4rem;
  }
`;

/**
 * Formulário de código + nova senha (etapa 2 da recuperação): o usuário
 * informa o código de 6 dígitos recebido por e-mail junto com a nova senha.
 * `documento` (CPF/CNPJ sem máscara) vem da etapa 1, já validado.
 */
export const ResetPasswordForm = ({ documento, onSuccess, onError, onVoltar, onTrocarEmail }) => {
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const getRequirements = () => ({
    minLength: novaSenha.length >= 8,
    match: novaSenha && confirmarSenha && novaSenha === confirmarSenha
  });

  const handleCodigoChange = (e) => {
    setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  const validate = () => {
    const newErrors = {};
    const requirements = getRequirements();

    if (codigo.length !== 6) {
      newErrors.codigo = 'Digite o código de 6 dígitos recebido por e-mail';
    }

    if (!novaSenha) {
      newErrors.novaSenha = 'Digite sua nova senha';
    } else if (!requirements.minLength) {
      newErrors.novaSenha = 'Senha deve ter no mínimo 8 caracteres';
    }

    if (!confirmarSenha) {
      newErrors.confirmarSenha = 'Confirme sua nova senha';
    } else if (novaSenha && confirmarSenha && novaSenha !== confirmarSenha) {
      newErrors.confirmarSenha = 'As senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      await resetSenhaService.confirmarReset(documento, codigo, novaSenha);
      onSuccess();
    } catch (err) {
      logger.error('Erro ao redefinir:', err);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const requirements = getRequirements();

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        label="Código de verificação"
        value={codigo}
        onChange={handleCodigoChange}
        error={errors.codigo}
        placeholder="000000"
        maxLength={6}
        required
        disabled={loading}
        helper="Código de 6 dígitos enviado por e-mail"
      />

      <PasswordInput
        label="Nova senha"
        value={novaSenha}
        onChange={(e) => setNovaSenha(e.target.value)}
        error={errors.novaSenha}
        placeholder="Digite sua nova senha"
        required
        disabled={loading}
      />

      <PasswordInput
        label="Confirmar senha"
        value={confirmarSenha}
        onChange={(e) => setConfirmarSenha(e.target.value)}
        error={errors.confirmarSenha}
        placeholder="Digite novamente"
        required
        disabled={loading}
      />

      <PasswordRequirements>
        <RequirementItem $valid={requirements.minLength}>
          <FaCheck size={12} />
          Mínimo de 8 caracteres
        </RequirementItem>
        <RequirementItem $valid={requirements.match}>
          <FaCheck size={12} />
          Senhas coincidem
        </RequirementItem>
      </PasswordRequirements>

      <Button
        type="submit"
        variant="primary"
        size="full"
        disabled={loading}
        leftIcon={<FaLock />}
      >
        {loading ? 'Redefinindo...' : 'Redefinir senha'}
      </Button>

      {onVoltar && (
        <Button
          type="button"
          variant="ghost"
          size="full"
          onClick={onVoltar}
          disabled={loading}
        >
          Não recebeu? Solicitar novo código
        </Button>
      )}

      {onTrocarEmail && (
        <Button
          type="button"
          variant="ghost"
          size="full"
          onClick={onTrocarEmail}
          disabled={loading}
        >
          Não tem mais acesso a este e-mail? Trocar e-mail
        </Button>
      )}
    </Form>
  );
};
